import {checkExhaustive} from 'external/dev_april_corgi+/js/common/asserts';

export type DurationUnit = 'd' | 'h' | 'm' | 's' | 'ms' | 'us';
export type VolumeUnit = 'l' | 'ml' | 'ul' | 'nl';

export const DURATION_IDS = ['d', 'h', 'm', 's', 'ms', 'us'] as const;
export const DURATION_LABELS = ['d', 'h', 'm', 's', 'ms', 'us'] as const;
export const VOLUME_IDS = ['l', 'ml', 'ul', 'nl'] as const;
export const VOLUME_LABELS = ['l', 'ml', 'μl', 'nl'] as const;

type ParsedUnit =
  | {kind: 'fraction'; top: ParsedUnit; bottom: ParsedUnit}
  | {kind: 'scaled'; scale: string; base: ParsedUnit}
  | {kind: 'simple'; value: string};

export function canonicalizeUnit(raw: string): string {
  return toString(canonicalize(parse(raw)), /* useMu= */ false);
}

export function formatUnit(raw: string): string {
  return toString(parse(raw), /* useMu= */ true);
}

function canonicalize(unit: ParsedUnit): ParsedUnit {
  if (unit.kind === 'fraction') {
    return {
      kind: 'fraction',
      top: canonicalize(unit.top),
      bottom: canonicalize(unit.bottom),
    };
  } else if (unit.kind === 'scaled') {
    return {kind: 'scaled', scale: unit.scale, base: canonicalize(unit.base)};
  } else if (unit.kind === 'simple') {
    const lower = unit.value.toLowerCase();
    let simple;
    if (['m', 'l', 'u'].indexOf(lower) >= 0) {
      simple = lower.toUpperCase();
    } else {
      // g, x, %
      simple = lower;
    }
    return {kind: 'simple', value: simple};
  } else {
    throw checkExhaustive(unit);
  }
}

export function harmonizeUnits(
  a: string,
  b: string,
): [aScale: number, bScale: number] | undefined {
  return harmonize(canonicalize(parse(a)), canonicalize(parse(b)));
}

function harmonize(
  a: ParsedUnit,
  b: ParsedUnit,
): [aScale: number, bScale: number] | undefined {
  if (a.kind === 'fraction' && b.kind === 'fraction') {
    const top = harmonize(a.top, b.top);
    const bottom = harmonize(a.bottom, b.bottom);
    if (top && bottom) {
      return [top[0] / bottom[0], top[1] / bottom[1]];
    } else {
      return undefined;
    }
  } else if (a.kind === 'scaled' && b.kind === 'scaled') {
    const base = harmonize(a.base, b.base);
    if (!base) {
      return undefined;
    }
    const left = getScale(a.scale) * base[0];
    const right = getScale(b.scale) * base[1];
    const min = Math.min(left, right);
    return [left / min, right / min];
  } else if (a.kind === 'scaled' && b.kind === 'simple') {
    const base = harmonize(a.base, b);
    if (!base) {
      return undefined;
    }
    return [getScale(a.scale), 1];
  } else if (a.kind === 'simple' && b.kind === 'scaled') {
    const base = harmonize(a, b.base);
    if (!base) {
      return undefined;
    }
    return [1, getScale(b.scale)];
  } else if (a.kind === 'simple' && b.kind === 'simple') {
    if (a.value === b.value) {
      return [1, 1];
    } else {
      return undefined;
    }
  } else {
    throw new Error(`Unexpected unit types ${a.kind} vs ${b.kind}`);
  }
}

export function nlToUnit(value: number, unit: VolumeUnit): number {
  if (unit === 'l') {
    return value / 1_000_000_000;
  } else if (unit === 'ml') {
    return value / 1_000_000;
  } else if ((unit as string) === 'μl' || unit === 'ul') {
    return value / 1_000;
  } else if (unit === 'nl') {
    return value;
  } else {
    throw checkExhaustive(unit);
  }
}

export function usToUnit(value: number, unit: DurationUnit): number {
  if (unit === 'd') {
    return value / 24 / 60 / 60 / 1_000_000;
  } else if (unit === 'h') {
    return value / 60 / 60 / 1_000_000;
  } else if (unit === 'm') {
    return value / 60 / 1_000_000;
  } else if (unit === 's') {
    return value / 1_000_000;
  } else if (unit === 'ms') {
    return value / 1_000;
  } else if ((unit as string) === 'μs' || unit === 'us') {
    return value;
  } else {
    throw checkExhaustive(unit);
  }
}

export function unitToNanos(
  number: number,
  unit: string,
): {number: number; unit: string} {
  return parsedUnitToNanos(number, parse(unit));
}

function parsedUnitToNanos(
  number: number,
  unit: ParsedUnit,
): {number: number; unit: string} {
  if (unit.kind === 'fraction') {
    const {number: topNumber, unit: topUnit} = parsedUnitToNanos(
      number,
      unit.top,
    );
    const {number: bottomNumber, unit: bottomUnit} = parsedUnitToNanos(
      1,
      unit.bottom,
    );
    return {
      number: topNumber / bottomNumber,
      unit: `${topUnit} / ${bottomUnit}`,
    };
  } else if (unit.kind === 'scaled') {
    return parsedUnitToNanos(number * getScale(unit.scale), unit.base);
  } else if (unit.kind === 'simple') {
    return {number: number * 1_000_000_000, unit: 'n' + unit.value};
  } else {
    throw checkExhaustive(unit);
  }
}

function parse(unit: string): ParsedUnit {
  if (/^([A-Za-z0-9μ]+)\s*\/\s*([A-Za-z0-9μ]+)$/.test(unit)) {
    const [top, bottom] = unit.split('/');
    return {
      kind: 'fraction',
      top: parse(top.trim()),
      bottom: parse(bottom.trim()),
    };
  } else if (/^[muμn](\w)$/.test(unit)) {
    return {kind: 'scaled', scale: withoutMu(unit[0]), base: parse(unit[1])};
  } else {
    return {kind: 'simple', value: unit.trim()};
  }
}

function toString(unit: ParsedUnit, useMu: boolean): string {
  if (unit.kind === 'fraction') {
    return `${toString(unit.top, useMu)} / ${toString(unit.bottom, useMu)}`;
  } else if (unit.kind === 'scaled') {
    return `${useMu ? withMu(unit.scale) : unit.scale}${toString(unit.base, useMu)}`;
  } else if (unit.kind === 'simple') {
    return unit.value;
  } else {
    throw checkExhaustive(unit);
  }
}

function getScale(s: string): number {
  if (s === 'm') {
    return 1 / 1000;
  } else if (s === 'u') {
    return 1 / 1000 / 1000;
  } else if (s === 'n') {
    return 1 / 1000 / 1000 / 1000;
  } else {
    throw new Error(`Unknown scale ${s}`);
  }
}

function withMu(s: string): string {
  const lower = s.toLowerCase();
  if (lower === 'u') {
    return 'μ';
  } else {
    return lower;
  }
}

function withoutMu(s: string): string {
  const lower = s.toLowerCase();
  if (lower === 'μ') {
    return 'u';
  } else {
    return lower;
  }
}
